import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material";
import { Eye } from "iconsax-react";
import { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { SefanoIconButton } from "@/components/Atoms";
import { formatPersianCurrency, formatPersianDate, formatPersianNumber } from "@/lib/format/persian";
import type { CustomersTableProps } from "./CustomersTable.types";
import * as styles from "./CustomersTable.styles";

export type { CustomersTableProps } from "./CustomersTable.types";

function getInitial(name: string): string {
  return name.trim().charAt(0) || "—";
}

function formatLastOrder(value?: string): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return formatPersianDate(date);
}

export const CustomersTable = memo(function CustomersTable({
  customers,
  isLoading = false,
  onRowClick,
}: CustomersTableProps) {
  const { t } = useTranslation("customers");

  const handleClick = useCallback(
    (customerId: number) => {
      onRowClick?.(customerId);
    },
    [onRowClick],
  );

  const handleActionClick = useCallback(
    (event: React.MouseEvent, customerId: number) => {
      event.stopPropagation();
      onRowClick?.(customerId);
    },
    [onRowClick],
  );

  if (isLoading) {
    return (
      <Box sx={styles.loadingWrap}>
        <CircularProgress size={36} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={styles.desktopTable}>
        <Table sx={styles.table}>
          <TableHead>
            <TableRow>
              <TableCell sx={styles.th}>{t("columns.customer")}</TableCell>
              <TableCell sx={styles.th}>{t("columns.phone")}</TableCell>
              <TableCell sx={styles.th}>{t("columns.orders")}</TableCell>
              <TableCell sx={styles.th}>{t("columns.totalSpent")}</TableCell>
              <TableCell sx={styles.th}>{t("columns.lastOrder")}</TableCell>
              <TableCell sx={styles.th} align="center">
                {t("columns.actions")}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customers.map((customer) => (
              <TableRow
                key={customer.id}
                sx={styles.row}
                onClick={() => handleClick(customer.id)}
              >
                <TableCell sx={styles.td}>
                  <Box sx={styles.nameCell}>
                    <Box sx={styles.avatar}>{getInitial(customer.full_name)}</Box>
                    <Typography variant="body2" fontWeight={500}>
                      {customer.full_name}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell sx={styles.phoneTd}>{customer.phone_number}</TableCell>
                <TableCell sx={styles.td}>
                  {customer.orders_count != null
                    ? formatPersianNumber(customer.orders_count)
                    : "—"}
                </TableCell>
                <TableCell sx={styles.amountTd}>
                  {customer.total_spent != null
                    ? formatPersianCurrency(customer.total_spent).replace(" ریال", "")
                    : "—"}
                </TableCell>
                <TableCell sx={styles.dateTd}>
                  {formatLastOrder(customer.last_order_at ?? customer.updated_at)}
                </TableCell>
                <TableCell sx={styles.td} align="center">
                  <Box sx={styles.actionsCell}>
                    <SefanoIconButton
                      aria-label={t("detail.title")}
                      onClick={(event) => handleActionClick(event, customer.id)}
                    >
                      <Eye size={17} variant="Linear" />
                    </SefanoIconButton>
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Box>

      <Box sx={styles.mobileList}>
        {customers.map((customer) => (
          <Box
            key={customer.id}
            sx={styles.mobileCard}
            onClick={() => handleClick(customer.id)}
          >
            <Box sx={styles.mobileCardTop}>
              <Box sx={styles.mobileCardCustomer}>
                <Box sx={styles.avatar}>{getInitial(customer.full_name)}</Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} noWrap>
                    {customer.full_name}
                  </Typography>
                  <Typography sx={styles.phone}>{customer.phone_number}</Typography>
                </Box>
              </Box>
              <SefanoIconButton
                aria-label={t("detail.title")}
                onClick={(event) => handleActionClick(event, customer.id)}
              >
                <Eye size={17} variant="Linear" />
              </SefanoIconButton>
            </Box>
            <Box sx={styles.mobileCardFooter}>
              <Typography variant="caption" color="text.secondary">
                {customer.orders_count != null
                  ? `${formatPersianNumber(customer.orders_count)} ${t("columns.orders")} · ${formatLastOrder(customer.last_order_at ?? customer.updated_at)}`
                  : formatLastOrder(customer.last_order_at ?? customer.updated_at)}
              </Typography>
              <Typography sx={styles.mobileAmount}>
                {customer.total_spent != null
                  ? formatPersianCurrency(customer.total_spent).replace(" ریال", "")
                  : "—"}{" "}
                {customer.total_spent != null ? (
                  <Typography component="span" sx={styles.mobileCurrency}>
                    ریال
                  </Typography>
                ) : null}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
    </>
  );
});
